import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Info, Pencil } from 'lucide-react'
import { TabBar, type TabBarItem } from '@/components/TabBar'
import { BillingLimitsSection, type BillingLimitsSectionProps } from './BillingLimitsSection'

type BillingProvider = 'claude' | 'copilot'

const PROVIDER_TABS: TabBarItem<BillingProvider>[] = [
  { key: 'claude', label: 'Claude Code' },
  { key: 'copilot', label: 'Copilot CLI' },
]

interface BillingProviderSectionProps {
  claudeBilling: BillingLimitsSectionProps
  copilotBilling: BillingLimitsSectionProps
  hasActiveCopilot: boolean
  isLoadingProviderState: boolean
  readOnly?: boolean
}

export function BillingProviderSection({
  claudeBilling,
  copilotBilling,
  hasActiveCopilot,
  isLoadingProviderState,
  readOnly = false,
}: BillingProviderSectionProps) {
  const [activeTab, setActiveTab] = useState<BillingProvider>('claude')
  const [isEditing, setIsEditing] = useState(false)
  const userSelected = useRef(false)

  const handleTabChange = (tab: BillingProvider) => {
    userSelected.current = true
    setIsEditing(false)
    setActiveTab(tab)
  }

  useEffect(() => {
    if (isLoadingProviderState) return
    if (userSelected.current) return
    setActiveTab(hasActiveCopilot ? 'copilot' : 'claude')
  }, [isLoadingProviderState, hasActiveCopilot])

  return (
    <div className="space-y-4">
      <div className="relative">
        <h2 className="text-xl font-bold mb-1">Billing &amp; Limits</h2>
        {!readOnly && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute top-0 right-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Edit billing settings"
            data-testid="spending-cap-edit-button"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        <p className="text-sm text-muted-foreground">
          Set a dollar-amount spending cap for this project. Workflows cannot be triggered unless a cap is explicitly defined. When a workflow execution would exceed the cap, it is blocked before execution begins.
        </p>
      </div>

      <TabBar
        tabs={PROVIDER_TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        className="w-fit"
      />

      <div role="tabpanel" data-testid={`billing-provider-${activeTab}-panel`}>
        {activeTab === 'claude' && (
          <BillingLimitsSection
            {...claudeBilling}
            isEditing={isEditing}
            onEditChange={setIsEditing}
          />
        )}

        {activeTab === 'copilot' && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              {!copilotBilling.isLoading && (copilotBilling.capUsd === null || copilotBilling.capUsd === undefined) && (
                <div
                  role="status"
                  className="flex items-start gap-2 rounded border border-warning-foreground bg-warning px-3 py-2 text-sm text-warning-foreground"
                  data-testid="copilot-billing-no-cap-banner"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <span className="font-semibold">No spending cap defined.</span>
                    <br />
                    <span>All Copilot workflows in this project are currently blocked. Set a cap to enable them.</span>
                  </div>
                </div>
              )}

              <div
                className="flex items-start gap-2 rounded border border-info-foreground bg-info px-3 py-2 text-sm text-info-foreground"
                data-testid="copilot-billing-info-note"
              >
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Copilot CLI reports usage in AI credits. <strong>1 AI credit = $0.01 USD.</strong> The spending cap is in USD and enforced against GitHub Copilot consumption for this project.
                </span>
              </div>
            </div>

            <BillingLimitsSection
              {...copilotBilling}
              isEditing={isEditing}
              onEditChange={setIsEditing}
              hideNoCap
            />
          </div>
        )}
      </div>
    </div>
  )
}
